import { ImDatabase } from 'react-icons/im';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { AgentApiAnalysisInsightsFunnelRow } from '@/types/serializers';
import InsightEmptyState from './InsightEmptyState';

// Recharts は SVG の fill を受け取るためインラインで持つが、値はデザインシステムの
// セマンティックトークン（Semantic/Primary、Semantic/Danger_Lighten）と一致させる。
const PASSED_COLOR = '#005FFF';
const DROPPED_COLOR = '#FFF0F0';
const AXIS_TEXT_MAIN = '#262626';
const AXIS_TEXT_SUB = '#6D6D6D';
const GRID_COLOR = '#DFE1E1';

// Recharts の TooltipProps 型は内部実装に依存して payload プロパティが欠ける場合があるため、
// content prop に渡すコンポーネント用に最小限のローカル型を定義する。
type ChartTooltipPayloadItem = {
  dataKey?: string | number;
  value?: number | string;
  color?: string;
  name?: string | number;
  payload?: Record<string, unknown>;
};

type ChartTooltipProps = {
  active?: boolean;
  payload?: ChartTooltipPayloadItem[];
};

interface Props {
  /** API から取得したファネル行（先頭が registration） */
  rows: AgentApiAnalysisInsightsFunnelRow[];
}

/**
 * Figma 準拠のホバーTooltip。通過 / 離脱 の件数を表示する。
 */
function FunnelChartTooltip({ active, payload }: ChartTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  const data = payload[0]?.payload as { passedCount?: number; droppedCount?: number } | undefined;
  if (!data) return null;
  const passedCount = data.passedCount ?? 0;
  const droppedCount = data.droppedCount ?? 0;

  return (
    <div className="flex flex-col gap-xxs rounded-sm bg-white p-xs text-body-xs text-main shadow-layer2">
      <div className="flex items-center gap-xxs">
        <span className="inline-block size-4 shrink-0 rounded-xs bg-primary" />
        <span>通過</span>
        <span className="font-bold">{passedCount}</span>
      </div>
      <div className="flex items-center gap-xxs">
        <span className="inline-block size-4 shrink-0 rounded-xs bg-danger_lighten" />
        <span>離脱</span>
        <span className="font-bold">{droppedCount}</span>
      </div>
    </div>
  );
}

/**
 * 進捗別ファネル図（通過/離脱を横方向の積み上げバーで表現）。
 * registration の通過数を 100% 基準として、各行を % に正規化して描画する。
 */
export default function InsightFunnelChart({ rows }: Props) {
  const baseTotal = rows[0]?.passed ?? 0;
  // baseTotal が 0 の場合はデータなしとみなし、空プレースホルダーを表示する
  const isEmpty = baseTotal === 0;

  const chartData = rows.map((row) => {
    const passedPct = baseTotal === 0 ? 0 : (row.passed / baseTotal) * 100;
    const droppedPct = baseTotal === 0 ? 0 : (row.dropped / baseTotal) * 100;
    return {
      label: row.label,
      passed: Math.round(passedPct * 10) / 10,
      dropped: Math.round(droppedPct * 10) / 10,
      passedCount: row.passed,
      droppedCount: row.dropped,
    };
  });

  if (isEmpty) {
    return (
      <div className="w-full">
        <h3 className="m-0 pb-sm text-heading-xs text-main">進捗別ファネル図</h3>
        <InsightEmptyState
          icon={ImDatabase}
          description="条件を変更すると、ここにファネル図が表示されます"
        />
      </div>
    );
  }

  return (
    <div className="w-full [&_.recharts-surface:focus:not(:focus-visible)]:outline-hidden [&_.recharts-wrapper_*:focus:not(:focus-visible)]:outline-hidden [&_.recharts-wrapper:focus:not(:focus-visible)]:outline-hidden">
      <h3 className="m-0 pb-sm text-heading-xs text-main">進捗別ファネル図</h3>

      {/* Figma 上、凡例はバーのStart位置（YAxis右側）に揃える。YAxis width と一致させる。 */}
      <div className="mb-sm flex items-center gap-xs pl-[72px] text-body-sm text-main">
        <div className="flex items-center gap-xs">
          <span className="inline-block size-[18px] shrink-0 rounded-sm bg-primary" />
          通過
        </div>
        <div className="flex items-center gap-xs">
          <span className="inline-block size-[18px] shrink-0 rounded-sm bg-danger_lighten" />
          離脱
        </div>
      </div>

      <ResponsiveContainer width="100%" height={360}>
        <BarChart
          layout="vertical"
          data={chartData}
          margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
          barCategoryGap="20%"
        >
          <CartesianGrid horizontal={false} strokeDasharray="4 4" stroke={GRID_COLOR} />
          <XAxis
            type="number"
            domain={[0, 100]}
            allowDataOverflow
            ticks={[0, 20, 40, 60, 80, 100]}
            tickFormatter={(v) => (v === 0 ? '0' : `${v}%`)}
            tick={{ fontSize: 12, fill: AXIS_TEXT_SUB }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="label"
            width={72}
            tick={{ fontSize: 12, fill: AXIS_TEXT_MAIN, textAnchor: 'end' }}
            tickMargin={8}
            interval={0}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip cursor={{ fill: 'rgba(0,95,255,0.05)' }} content={<FunnelChartTooltip />} />
          <Bar
            dataKey="passed"
            stackId="funnel"
            fill={PASSED_COLOR}
            isAnimationActive={false}
            radius={[3, 3, 3, 3]}
          />
          <Bar
            dataKey="dropped"
            stackId="funnel"
            fill={DROPPED_COLOR}
            isAnimationActive={false}
            radius={[0, 3, 3, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
